<template>
  <!-- C-12 (XOL-596 UX addendum, 2026-10-13): the ONE save-status card of the app.
       It shows the whole save cycle (Changes pending -> Saving… -> Saved) and, as
       sections of the SAME card, every XOL-596 notice (the C-1 body). Never a second
       card, never $notify. position: fixed, z-index 3990 (above LayerHost 3010-3051,
       below Element's PopupManager base 4000, so the MessageBox dialogs, the masks and
       the "Copied" message stay above it). While another popup is open (the Configure
       Emails drawer is one) the card is raised ABOVE it (`zIndex`), so a conflict
       raised from inside that popup can be read; a confirm dialog still goes over the
       card. Never takes focus. -->
  <div class="xol-save-popup-host">
    <transition name="xol-ssp" :css="!reduced">
      <div
        v-show="shown"
        class="xol-save-popup"
        :class="cardClass"
        :style="{ pointerEvents: interactive ? 'auto' : 'none', zIndex: zIndex }"
        role="region"
        :aria-label="label">

        <div class="ssp-head">
          <i class="ssp-icon" :class="view.icon" aria-hidden="true"></i>
          <div :key="view.key" class="ssp-title" :class="{ 'ssp-title--err': view.err }" :title="view.plain">
            <span class="ssp-title-text">{{view.text}}</span>
            <span v-if="view.hint" class="ssp-hint">{{view.hint}}</span>
            <el-button v-if="view.tryAgain" class="ssp-try" type="text" size="mini" @click="tryAgainUnsaved">{{retryLabel}}</el-button>
          </div>
        </div>

        <!-- The sections (C-1 bodies). The wrapper opens by grid-template-rows, so
             the card morphs in place instead of swapping. -->
        <div class="ssp-wrap" :class="{ 'is-open': hasSections }">
          <div class="ssp-body">
            <div class="ssp-inner">
              <div v-for="(s, i) in bodySections" :key="s.id" class="ssp-section" :class="{ 'ssp-section--next': i > 0 }">
                <div v-if="i > 0" class="ssp-section-head">
                  <i :class="iconOf(s.type)" class="ssp-section-icon" :data-type="s.type" aria-hidden="true"></i>
                  <span>{{s.title}}</span>
                </div>
                <stale-save-notice
                  :live="false"
                  :groups="s.groups"
                  :lead="s.lead"
                  :lines="s.lines"
                  :footer="s.footer"
                  :saved-line="s.savedLine"
                  :on-dismiss="dismissOf(s)"
                  :on-retry="retryOf(s)" />
              </div>
            </div>
          </div>
        </div>

        <!-- The new cycle (or the retry loop) while a section shows, or the Saved
             that a new edit interrupted. -->
        <div v-if="strip" class="ssp-strip" :class="'ssp-strip--' + strip.kind">
          <i :class="strip.icon" aria-hidden="true"></i>
          <span class="ssp-strip-text">{{strip.text}}</span>
        </div>

        <div v-if="showBar" class="ssp-bar" aria-hidden="true">
          <span :key="activity.pauseStartedAt" class="ssp-bar-fill" :style="barStyle()"></span>
        </div>
      </div>
    </transition>

    <!-- The two live regions (siblings of the card, always present). -->
    <div class="ssp-sr" role="status" aria-live="polite" aria-atomic="true">{{politeText}}</div>
    <div class="ssp-sr" role="alert" aria-live="assertive" aria-atomic="true">{{assertiveText}}</div>
  </div>
</template>

<script>
import { Button } from 'element-ui'
import * as sync from '@/lib/requestRowSync'
import * as popup from '@/lib/savePopup'
import { copyText } from '@/lib/requestSyncCopy'
import StaleSaveNotice from '@/components/requests/StaleSaveNotice'
import { PopupManager } from 'element-ui/lib/utils/popup'

// The card's own level: above LayerHost (3010-3051), below Element's PopupManager base.
var BASE_Z = 3990

// While the card shows, how often it re-reads Element's popup stack (see `watchStack`).
var STACK_CHECK_MS = 250

// After the last section goes, the body keeps its content this long so it can
// collapse instead of vanishing.
var COLLAPSE_MS = 260

var ICONS = {
  warning: 'el-icon-warning',
  error: 'el-icon-error',
  info: 'el-icon-info'
}

export default {
  name: 'save-status-popup',

  components: { ElButton: Button, StaleSaveNotice: StaleSaveNotice },

  data () {
    return {
      // 'drawer' (top 6 / right 62, left of the LayerHost X) or 'page' (top 16 /
      // right 16). Decided each time the card goes hidden -> shown.
      placement: 'page',
      // Where the card sits in the stack (see `raise`).
      zIndex: BASE_Z,
      looseSavingShown: false,
      reduced: false,
      now: Date.now(),
      lastView: null,
      bodySections: [],
      politeText: '',
      assertiveText: '',
      label: copyText('requestSync.popup.label'),
      retryLabel: copyText('requestSync.status.retry')
    }
  },

  computed: {
    activity () { return sync.syncState.activity },

    store () { return popup.state },

    sections () { return popup.state.sections },

    hasSections () { return this.sections.length > 0 },

    // A 428 was seen: nothing can be saved, the C-8 banner is the signal.
    blocked () { return sync.syncState.versionRequiredSeen },

    // View-only users never see the card.
    suppressed () {
      var r = this.$root
      return !!(r && typeof r.isViewOnly === 'function' && r.isViewOnly())
    },

    cycle () {
      if (this.suppressed) {
        return null
      }
      return popup.cycleOf(this.activity, this.store.leaving, this.blocked, this.looseSavingShown)
    },

    looseSaving () {
      return this.activity.saving && !this.activity.savingScoped
    },

    savedActive () { return !this.suppressed && !!this.store.saved },

    unsavedCount () { return this.blocked || this.suppressed ? 0 : this.activity.unsaved },

    // The head of the card, by precedence: error sections > leaving > saving >
    // pending > saved > unsaved > hidden.
    head () {
      if (this.suppressed) {
        return null
      }
      var s = this.sections[0]

      if (s) {
        return { cls: 'is-' + s.type, icon: this.iconOf(s.type), text: s.title, plain: s.title + (s.lead ? '. ' + s.lead : ''), hint: null, err: true, key: 'sec' + s.id + ':' + s.title, tryAgain: false }
      }
      if (this.cycle === 'leaving') {
        return this.cycleHead('is-saving', 'el-icon-loading', popup.leavingText(), 'leaving')
      }
      if (this.cycle === 'saving') {
        return this.cycleHead('is-saving', 'el-icon-loading', copyText('requestSync.status.saving'), 'saving')
      }
      if (this.cycle === 'pending') {
        var t = copyText('requestSync.status.pending')
        var h = this.cycleHead('is-pending', 'el-icon-time', t, 'pending')
        h.hint = copyText('requestSync.popup.pendingHint')
        h.plain = t
        return h
      }
      if (this.savedActive) {
        return this.cycleHead('is-saved', 'el-icon-success', this.store.saved.text, 'saved')
      }
      if (this.unsavedCount > 0) {
        var u = this.cycleHead('is-unsaved', 'el-icon-error', copyText('requestSync.status.unsaved', { n: this.unsavedCount }), 'unsaved')
        u.tryAgain = true
        return u
      }
      return null
    },

    // What the card shows while it fades out.
    view () {
      return this.head || this.lastView || { cls: '', icon: '', text: '', plain: '', hint: null, err: false, key: 'none', tryAgain: false }
    },

    shown () { return !!this.head },

    interactive () {
      return this.hasSections || (!!this.head && this.head.tryAgain)
    },

    // The line under the head: the retry loop of a blocked close, the new cycle
    // while a section shows, or the Saved a new edit interrupted.
    strip () {
      if (this.hasSections) {
        var r = this.store.retry
        if (r) {
          return r.running
            ? { kind: 'retry', icon: 'el-icon-loading', text: copyText('requestSync.popup.retrying', { n: r.attempt }) }
            : { kind: 'retry', icon: 'el-icon-refresh', text: copyText('requestSync.popup.retryIn', { s: this.retrySeconds(r), n: r.attempt }) }
        }
        return this.cycleStrip()
      }
      // No section: the Saved that a new edit interrupted drops into the strip for
      // the rest of its 3 s.
      return this.cycle === 'pending' && this.savedActive ? this.savedStrip() : null
    },

    // The countdown bar: under a pending head, or under a pending strip.
    showBar () {
      if (this.reduced || !this.head) {
        return false
      }
      return this.hasSections ? !!this.strip && this.strip.kind === 'pending' : this.cycle === 'pending'
    },

    cardClass () {
      var c = ['xol-save-popup--' + this.placement, this.view.cls]
      if (this.hasSections) { c.push('is-err') }
      if (this.strip) { c.push('with-strip') }
      if (this.showBar) { c.push('with-bar') }
      if (this.reduced) { c.push('is-reduced') }
      return c
    }
  },

  watch: {
    head: {
      immediate: true,
      handler (h) {
        if (h) {
          this.lastView = h
        }
      }
    },

    // Decided when the card goes hidden -> shown; it does not jump if the drawer
    // closes while it is up.
    shown (now, before) {
      if (now && !before) {
        this.placement = this.drawerPresent() ? 'drawer' : 'page'
      }
      if (now) {
        this.raise()
      }
      this.watchStack(now)
    },

    // A new section can arrive while the card is already up, and from inside a popup
    // that opened after it.
    'sections.length' () {
      this.raise()
    },

    sections: {
      immediate: true,
      deep: true,
      handler (list) {
        clearTimeout(this.collapseTimer)
        if (list.length > 0) {
          this.bodySections = list.slice()
        } else {
          var self = this
          this.collapseTimer = setTimeout(function () {
            self.bodySections = []
          }, COLLAPSE_MS)
        }
      }
    },

    // "Saving…" outside a drawer scope (EditPane Save) only after 300 ms.
    looseSaving: {
      immediate: true,
      handler (on) {
        clearTimeout(this.looseTimer)
        if (on) {
          var self = this
          this.looseTimer = setTimeout(function () {
            self.looseSavingShown = true
          }, popup.LOOSE_SAVING_MS)
        } else {
          this.looseSavingShown = false
        }
      }
    },

    // Announcements of the cycle: Pending once per cycle, Saving only if it is
    // still going after 1 s. (Leaving, Saved and the sections are spoken by the
    // store when they are raised.)
    cycle (now, before) {
      clearTimeout(this.sayTimer)
      if (now === 'pending' && before !== 'pending') {
        popup.say(copyText('requestSync.status.pending'), false)
      } else if (now === 'saving') {
        var self = this
        this.sayTimer = setTimeout(function () {
          if (self.cycle === 'saving') {
            popup.say(copyText('requestSync.status.saving'), false)
          }
        }, popup.SAVING_ANNOUNCE_MS)
      }
    },

    'store.polite.n' () {
      this.speak('politeText', this.store.polite.text)
    },

    'store.assertive.n' () {
      this.speak('assertiveText', this.store.assertive.text)
    },

    // The 1 s tick of "Retrying in {s} s".
    'store.retry': {
      immediate: true,
      handler (r) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
        if (r && !r.running) {
          var self = this
          this.now = Date.now()
          this.tickTimer = setInterval(function () { self.now = Date.now() }, 1000)
        }
      }
    }
  },

  created () {
    this.collapseTimer = null
    this.looseTimer = null
    this.sayTimer = null
    this.tickTimer = null
    this.mql = null

    // prefers-reduced-motion: no transitions, no animations, no countdown bar
    // (the "saves when you pause" hint carries the meaning).
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      var self = this
      this.mql = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.reduced = !!(this.mql && this.mql.matches)
      this.onMotion = function (e) { self.reduced = !!e.matches }
      if (this.mql && typeof this.mql.addEventListener === 'function') {
        this.mql.addEventListener('change', this.onMotion)
      } else if (this.mql && typeof this.mql.addListener === 'function') {
        this.mql.addListener(this.onMotion)
      }
    }
  },

  beforeDestroy () {
    this.watchStack(false)
    clearTimeout(this.collapseTimer)
    clearTimeout(this.looseTimer)
    clearTimeout(this.sayTimer)
    clearInterval(this.tickTimer)
    if (this.mql && this.onMotion) {
      if (typeof this.mql.removeEventListener === 'function') {
        this.mql.removeEventListener('change', this.onMotion)
      } else if (typeof this.mql.removeListener === 'function') {
        this.mql.removeListener(this.onMotion)
      }
    }
  },

  methods: {
    iconOf (type) {
      return ICONS[type] || ICONS.warning
    },

    cycleHead (cls, icon, text, kind) {
      return { cls: cls, icon: icon, text: text, plain: text, hint: null, err: false, key: kind + ':' + text, tryAgain: false }
    },

    // The strip line of the current cycle (a section is showing).
    cycleStrip () {
      if (this.cycle === 'leaving') {
        return { kind: 'leaving', icon: 'el-icon-loading', text: popup.leavingText() }
      }
      if (this.cycle === 'saving') {
        return { kind: 'saving', icon: 'el-icon-loading', text: copyText('requestSync.status.saving') }
      }
      if (this.cycle === 'pending') {
        return { kind: 'pending', icon: 'el-icon-time', text: copyText('requestSync.status.pending') }
      }
      return this.savedActive ? this.savedStrip() : null
    },

    savedStrip () {
      return { kind: 'saved', icon: 'el-icon-success', text: this.store.saved.text }
    },

    retrySeconds (r) {
      return Math.max(0, Math.ceil(((r.nextAt || this.now) - this.now) / 1000))
    },

    barStyle () {
      var a = this.activity
      var ms = a.pauseMs
      var elapsed = a.pauseStartedAt ? Math.min(ms, Math.max(0, Date.now() - a.pauseStartedAt)) : 0
      return { animationDuration: ms + 'ms', animationDelay: '-' + elapsed + 'ms' }
    },

    // Element hands every popup (a dialog, a drawer, a message box) the next z-index
    // from one climbing counter, starting at 4000, so a card fixed at 3990 sits under the
    // mask of ANY open popup: a recipients conflict raised from inside the Configure
    // Emails popup could not be read until it was closed. So when a popup is open the
    // card takes the next index from the SAME counter (above everything open now, and
    // below whatever opens later). A confirm (the hang, connection, logout and 428
    // dialogs, the approval-lock prompt) is the exception: it is meant to go over the
    // card, so while one is up the card stays at its own level.
    raise () {
      if (typeof document === 'undefined') {
        return
      }

      var modals = PopupManager.modalStack || []

      if (modals.length === 0 || this.confirmOpen()) {
        this.zIndex = BASE_Z
        return
      }

      // The stack holds each popup's MASK; its dialog sits one above.
      var top = modals[modals.length - 1].zIndex + 1

      if (!(this.zIndex > top)) {
        this.zIndex = PopupManager.nextZIndex()
      }
    },

    // XOL-596: `raise` runs when the card shows and when a section is added, but Element's popup
    // stack is not reactive: a popup that opens AFTER the card is up (Configure Emails, with a
    // notice already showing), or a section replaced in place (same count), left the card under
    // the new mask until that popup closed. So while the card shows, it looks at the stack
    // again every STACK_CHECK_MS. Idle, nothing runs.
    watchStack (on) {
      clearInterval(this.stackTimer)
      this.stackTimer = null

      if (on) {
        var self = this

        this.stackTimer = setInterval(function () { self.raise() }, STACK_CHECK_MS)
      }
    },

    // Is a message box or the approval-lock prompt on screen?
    confirmOpen () {
      var boxes = document.querySelectorAll('.el-message-box__wrapper, .xol-lock-dialog')

      for (var i = 0; i < boxes.length; i++) {
        var wrapper = boxes[i].classList.contains('el-message-box__wrapper') ? boxes[i] : boxes[i].parentNode

        if (wrapper && wrapper.style.display !== 'none') {
          return true
        }
      }

      return false
    },

    drawerPresent () {
      return typeof document !== 'undefined' && !!document.querySelector('.layer-host__drawer')
    },

    // Cleared, then set on the next tick, so a repeated text is spoken again.
    speak (field, text) {
      var self = this
      this[field] = ''
      this.$nextTick(function () {
        self[field] = text
      })
    },

    dismissOf (s) {
      return function () { popup.dismiss(s.id) }
    },

    // Try again of a network section: skip the countdown of a waiting retry loop,
    // else the section's own handler, else flush every failed field.
    retryOf (s) {
      if (!s.retry) {
        return null
      }
      return function () {
        if (popup.retryNowIfWaiting()) {
          return
        }
        if (s.onRetry) {
          s.onRetry()
        } else {
          sync.flushAllAndWait({ reason: 'retry' })
        }
      }
    },

    tryAgainUnsaved () {
      sync.flushAllAndWait({ reason: 'retry' })
    }
  }
}
</script>

<style scoped>
.xol-save-popup {
  position: fixed;
  z-index: 3990;
  width: 330px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #EBEEF5;
  border-left: 4px solid #909399;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .1);
  overflow: hidden;
  font-size: 13px;
  color: #303133;
  transition: border-color .2s;
}
.xol-save-popup--drawer { top: 6px; right: 62px; max-width: calc(100vw - 78px); }
.xol-save-popup--page { top: 16px; right: 16px; max-width: calc(100vw - 32px); }

.xol-ssp-enter-active, .xol-ssp-leave-active { transition: opacity .2s ease-out, transform .2s ease-out, border-color .2s; }
.xol-ssp-enter, .xol-ssp-leave-to { opacity: 0; transform: translateX(16px); }

.is-pending { border-left-color: #909399; }
.is-saving { border-left-color: #409EFF; }
.is-saved { border-left-color: #67C23A; }
.is-warning { border-left-color: #E6A23C; }
.is-error, .is-unsaved { border-left-color: #F56C6C; }
.is-info { border-left-color: #909399; }

.ssp-head { display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px 9px 10px; min-height: 38px; box-sizing: border-box; }
.ssp-icon { font-size: 16px; line-height: 19px; flex: none; transition: color .2s; }
.is-pending .ssp-icon, .is-info .ssp-icon { color: #909399; }
.is-saving .ssp-icon { color: #409EFF; }
.is-saved .ssp-icon { color: #67C23A; }
.is-warning .ssp-icon { color: #E6A23C; }
.is-error .ssp-icon, .is-unsaved .ssp-icon { color: #F56C6C; }

.ssp-title { flex: 1; min-width: 0; line-height: 19px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; animation: ssp-fade .15s ease-out; }
.is-pending .ssp-title { color: #606266; }
.ssp-title--err { font-size: 16px; font-weight: bold; white-space: normal; }
.ssp-hint { color: #909399; }
.ssp-try { padding: 0 0 0 6px; }

.ssp-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .22s ease-out; }
.ssp-wrap.is-open { grid-template-rows: 1fr; }
.ssp-body { min-height: 0; overflow: hidden; }
.ssp-inner { padding: 0 12px 10px 34px; max-height: calc(70vh - 60px); overflow-y: auto; color: #606266; line-height: 1.4; }
.ssp-section--next { margin-top: 8px; padding-top: 8px; border-top: 1px solid #EBEEF5; }
.ssp-section-head { display: flex; gap: 8px; align-items: flex-start; margin: 0 0 6px -22px; font-size: 14px; font-weight: bold; color: #303133; }
.ssp-section-icon[data-type="warning"] { color: #E6A23C; }
.ssp-section-icon[data-type="error"] { color: #F56C6C; }
.ssp-section-icon[data-type="info"] { color: #909399; }

.ssp-strip { border-top: 1px solid #EBEEF5; padding: 6px 12px 6px 34px; font-size: 12px; color: #606266; }
.ssp-strip .el-icon-success { color: #67C23A; }
.ssp-strip .el-icon-loading { color: #409EFF; }

.ssp-bar { height: 3px; background: #EBEEF5; }
.ssp-bar-fill { display: block; height: 100%; background: #909399; transform-origin: left center; animation-name: ssp-drain; animation-timing-function: linear; animation-fill-mode: forwards; }

.ssp-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }

@keyframes ssp-fade { from { opacity: .2; } to { opacity: 1; } }
@keyframes ssp-drain { from { transform: scaleX(1); } to { transform: scaleX(0); } }

@media (max-width: 479px) {
  .ssp-hint { display: none; }
  .ssp-inner { max-height: calc(60vh - 60px); }
}

.is-reduced, .is-reduced * { transition: none !important; animation: none !important; }

@media (prefers-reduced-motion: reduce) {
  .xol-save-popup, .xol-save-popup * { transition: none !important; animation: none !important; }
  .ssp-bar { display: none !important; }
}
</style>
